import React, {
	useState,
	useMemo,
	useCallback,
	useEffect,
	useRef,
} from "react";
import MultipleContainers from "../../../Common/DragAndDrop/MultipleContainers";
import { Dialog, Zoom, IconButton, Box, Typography } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import {
	languages as languagesConst,
	syncTranslationKeys,
} from "./utils/constants";
import TranslationsTable from "../../../Translations/TranslationsTable";
import { SegmentItem } from "./components/SegmentItem";
import { SegmentHeader } from "./components/SegmentHeader";
import { computeModalTranslations } from "./utils/helpers";
import { baseUrl } from "./services/checklistTemplatesApisService";
import SaveNewItemsConfirmationModal from "./components/SaveNewItemsConfirmationModal";

export default function SegmentsCreation({
	resources,
	segments,
	translationsData,
	setSegments,
	segmentData,
	setSegmentData,
	isEditMode,
	isTranslationsPreview,
	selectedLang,
	onTranslationsModalClose,
	onSaveNewItems,
	validateFields,
	hasTranslationLicence
}) {
	const [hasSyncChanges, setHasSyncChanges] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [currentSegmentContainerId, setCurrentSegmentContainerId] =
		useState(null);

	const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
	const [pendingEditSegment, setPendingEditSegment] = useState(null);
	const [confirmationLoading, setConfirmationLoading] = useState(false);

	// Track touched state of segments and items
	const [touchedSegments, setTouchedSegments] = useState({});

	// Track segments with validation errors
	const [containersWithErrors, setContainersWithErrors] = useState({});
	const [containersErrorMessages, setContainersErrorMessages] = useState({});

	// Track if we've just added a new segment to prevent unwanted accordion opening
	const isAddingNewSegmentRef = useRef(false);

	// Track if validation was triggered by form submission - always reset on new validation
	const [formValidationTriggered, setFormValidationTriggered] = useState(false);
	const [lastValidationId, setLastValidationId] = useState(null);

	useEffect(() => {
		// Skip if validation ID hasn't changed or it's null, or in preview mode
		if (
			!validateFields ||
			validateFields === lastValidationId ||
			isTranslationsPreview
		) {
			return;
		}

		setLastValidationId(validateFields);
		setFormValidationTriggered(true);

		const allTouched = {};
		const newInvalidContainers = {};
		const newContainerErrorMessages = {};

		Object.keys(segments).forEach((containerId) => {
			allTouched[containerId] = { header: true, items: {} };

			const segmentHeaderValid =
				segmentData[containerId]?.description?.trim() || false;

			if (!segmentHeaderValid) {
				newInvalidContainers[containerId] = true;
			}

			if(segments[containerId].length === 0) {
				newInvalidContainers[containerId] = true;
				newContainerErrorMessages[containerId] = resources['SegmentShouldHaveAtLeastOneItem']
			}

			segments[containerId].forEach((itemId) => {
				allTouched[containerId].items[itemId] = {
					shortDescription: true,
					longDescription: true,
					responseType: true,
				};

				const itemData = segmentData[containerId]?.items?.[itemId] || {};
				const itemValid =
					itemData.shortDescription?.trim() &&
					itemData.longDescription?.trim() &&
					itemData.responseType > 0;

				if (!itemValid) {
					newInvalidContainers[containerId] = true;
				}
			});
		});

		setTouchedSegments(allTouched);
		setContainersWithErrors(newInvalidContainers);
		setContainersErrorMessages(newContainerErrorMessages);

		// Always reset user closed accordions on new validation
		setUserClosedAccordions({});
	}, [
		validateFields,
		segments,
		segmentData,
		isTranslationsPreview,
		lastValidationId,
	]);

	// Reset form validation flag when cleanup (important for re-validation)
	useEffect(() => {
		return () => {
			setFormValidationTriggered(false);
		};
	}, [validateFields]);

	// Keep track of which accordions the user manually closed
	const [userClosedAccordions, setUserClosedAccordions] = useState({});

	// Updated to better handle toggling
	const handleAccordionToggle = (containerId, isExpanded) => {
		if (!isExpanded) {
			// Only record manual closures when not in validation mode
			if (!formValidationTriggered) {
				setUserClosedAccordions((prev) => ({
					...prev,
					[containerId]: true,
				}));
			}
		} else {
			setUserClosedAccordions((prev) => {
				const updated = { ...prev };
				delete updated[containerId];
				return updated;
			});
		}
	};

	const handleItemsChange = (newItems) => {
		// If we have a new segment
		if (Object.keys(newItems).length > Object.keys(segments).length) {
			isAddingNewSegmentRef.current = true;

			// Reset the flag after a short delay to allow the UI to update
			setTimeout(() => {
				isAddingNewSegmentRef.current = false;
			}, 500);
		}

		setSegments(newItems);
	};

	function handleSegmentDescChange(containerId, value) {
		setSegmentData((prev) => ({
			...prev,
			[containerId]: {
				...prev[containerId],
				description: value,
			},
		}));

		// Mark as touched when changed
		setTouchedSegments((prev) => ({
			...prev,
			[containerId]: {
				...(prev[containerId] || {}),
				header: true,
			},
		}));
	}

	function handleSegmentActiveChange(containerId, checked) {
		setSegmentData((prev) => ({
			...prev,
			[containerId]: {
				...prev[containerId],
				isActive: checked,
			},
		}));
	}

	function handleItemChange(containerId, itemId, field, value) {
		setSegmentData((prev) => ({
			...prev,
			[containerId]: {
				...prev[containerId],
				items: {
					...(prev[containerId]?.items || {}),
					[itemId]: {
						...(prev[containerId]?.items?.[itemId] || {}),
						[field]: value,
					},
				},
			},
		}));

		setTouchedSegments((prev) => ({
			...prev,
			[containerId]: {
				...(prev[containerId] || {}),
				items: {
					...(prev[containerId]?.items || {}),
					[itemId]: {
						...(prev[containerId]?.items?.[itemId] || {}),
						[field]: true,
					},
				},
			},
		}));
	}

	const handleItemResponseTypeChange = (containerId, itemId, value) => {
		handleItemChange(containerId, itemId, "responseType", value);
	};

	const handleItemActiveChange = (containerId, itemId, checked) => {
		handleItemChange(containerId, itemId, "isActive", checked);
	};

	const syncTranslation = () => {
		setHasSyncChanges(true);
	};

	const deleteTranslation = () => {
		setHasSyncChanges(true);
	};

	const handleEditTranslations = (containerId) => {
		const segData = segmentData[containerId];
		const segmentIsComplete = isSegmentComplete(segData);

		// Filter out new items
		const items = segments[containerId] || [];
		const newItems = items.filter((itemId) => typeof itemId !== "number");

		// Check if all new items are complete.
		let newItemsAreComplete = true;
		if (newItems.length > 0) {
			newItemsAreComplete = newItems.every((itemId) => {
				const itemData = segData?.items ? segData.items[itemId] : null;
				return isItemComplete(itemData);
			});
		}

		if (segmentIsComplete && newItems.length > 0 && newItemsAreComplete) {
			setPendingEditSegment(containerId);
			setShowSaveConfirmation(true);
		} else {
			setCurrentSegmentContainerId(containerId);
			setEditModalOpen(true);
		}
	};

	// When the user confirms saving new items
	const handleConfirmSaveNewItems = async () => {
		setConfirmationLoading(true);

		const containerIds = Object.keys(segments);

		const indexOfPending = containerIds.findIndex(
			(containerId) => containerId === pendingEditSegment
		);

		// Call onSaveNewItems to save the new items
		const newSegments = await onSaveNewItems();
		const newSegmentsIds = Object.keys(newSegments);

		const translationsModalSegmentId = newSegmentsIds[indexOfPending];

		setConfirmationLoading(false);
		setShowSaveConfirmation(false);

		// Now open the edit translations modal for the pending segment.
		setCurrentSegmentContainerId(translationsModalSegmentId);
		setEditModalOpen(true);
		setPendingEditSegment(null);
	};

	const modalTranslations = useMemo(() => {
		return computeModalTranslations(
			currentSegmentContainerId,
			translationsData,
			segments
		);
	}, [currentSegmentContainerId, translationsData, segments]);

	const isItemComplete = (itemData) => {
		return (
			itemData?.longDescription?.trim() &&
			itemData?.shortDescription?.trim() &&
			itemData.responseType > 0
		);
	};

	const isSegmentComplete = (segData) => {
		return segData?.description?.trim();
	};

	const areAllSegmentsComplete = useMemo(() => {
		return Object.keys(segments).every((containerId) => {
			const segData = segmentData[containerId];
			return isSegmentComplete(segData);
		});
	}, [segments, segmentData]);

	const areAllItemsInSegmentComplete = useCallback(
		(containerId) => {
			const items = segments[containerId] || [];
			const segData = segmentData[containerId];

			return items.every((itemId) => {
				const itemData = segData?.items ? segData.items[itemId] : null;
				return isItemComplete(itemData);
			});
		},
		[segments, segmentData]
	);

	const filteredSegments = useMemo(() => {
		// If not in preview mode, return all segments unchanged.
		if (!isTranslationsPreview) {
			return segments;
		}

		const result = {};
		Object.keys(segments).forEach((containerId) => {
			const segData = segmentData[containerId];
			// Only include the segment if its header (description) is complete.
			if (!isSegmentComplete(segData)) {
				return;
			}

			// Filter out items that are not complete
			const filteredItems = segments[containerId].filter((itemId) => {
				const itemData = segData.items ? segData.items[itemId] : null;
				return isItemComplete(itemData);
			});

			// Only include the segment if it has at least one complete item.
			if (filteredItems.length > 0) {
				result[containerId] = filteredItems;
			}
		});

		return result;
	}, [segments, segmentData, isTranslationsPreview]);

	const evaluateContainersWithErrors = useCallback(() => {
		if (isTranslationsPreview) {
			return;
		}

		const newContainersWithErrors = {};
		const newContainerErrorMessages = {};

		Object.keys(segments).forEach((containerId) => {
			let hasErrors = false;

			// Check if segment header is valid
			const segmentHeaderValid =
				segmentData[containerId]?.description?.trim() || false;
			const touchedHeader = touchedSegments[containerId]?.header;

			if (touchedHeader && !segmentHeaderValid) {
				hasErrors = true;
			}

			if(segments[containerId].length === 0) {
				newContainersWithErrors[containerId] = true
				newContainerErrorMessages[containerId] = resources['SegmentShouldHaveAtLeastOneItem']
			}

			// Check if any items in this segment have validation errors
			segments[containerId].forEach((itemId) => {
				const itemData = segmentData[containerId]?.items?.[itemId] || {};
				const touchedItemFields =
					touchedSegments[containerId]?.items?.[itemId] || {};

				// Check each required field that has been touched
				if (
					(touchedItemFields.shortDescription &&
						!itemData.shortDescription?.trim()) ||
					(touchedItemFields.longDescription &&
						!itemData.longDescription?.trim()) ||
					(touchedItemFields.responseType &&
						(!itemData.responseType || itemData.responseType === 0))
				) {
					hasErrors = true;
				}
			});

			if (hasErrors) {
				newContainersWithErrors[containerId] = true;
			}
		});

		setContainersWithErrors(newContainersWithErrors);
		setContainersErrorMessages(newContainerErrorMessages);
	}, [segments, segmentData, touchedSegments, isTranslationsPreview]);

	// Update error containers whenever relevant data changes
	useEffect(() => {
		evaluateContainersWithErrors();
	}, [touchedSegments, evaluateContainersWithErrors]);

	return (
		<>
			<MultipleContainers
				resources={resources}
				items={filteredSegments}
				onItemsChange={handleItemsChange}
				isPreviewMode={isTranslationsPreview}
				canAddContainer={areAllSegmentsComplete}
				canAddContainerItem={areAllItemsInSegmentComplete}
				renderHeader={(containerId, isOverlay = false) => {
					const seg = segmentData[containerId] || {};
					return (
						<SegmentHeader
							containerId={containerId}
							isOverlay={isOverlay}
							isTranslationsPreview={isTranslationsPreview}
							selectedLang={selectedLang}
							seg={seg}
							translationsData={translationsData}
							onHeaderBlur={handleSegmentDescChange}
							onActiveChange={handleSegmentActiveChange}
							onEditTranslations={handleEditTranslations}
							resources={resources}
							touched={touchedSegments[containerId]?.header}
						/>
					);
				}}
				renderItem={(itemId, containerId, isOverlay = false) => {
					const seg = segmentData[containerId] || {};
					const it = (seg.items || {})[itemId] || {};
					const touchedInfo = touchedSegments[containerId]?.items?.[itemId];

					return (
						<SegmentItem
							containerId={containerId}
							itemId={itemId}
							isOverlay={isOverlay}
							isTranslationsPreview={isTranslationsPreview}
							isEditMode={isEditMode}
							segItem={it}
							translationsData={translationsData}
							selectedLang={selectedLang}
							onItemBlur={handleItemChange}
							onResponseTypeChange={handleItemResponseTypeChange}
							onActiveChange={handleItemActiveChange}
							resources={resources}
							touchedFields={{
								shortDescription: touchedInfo?.shortDescription || false,
								longDescription: touchedInfo?.longDescription || false,
								responseType: touchedInfo?.responseType || false,
							}}
						/>
					);
				}}
				containersWithErrors={containersWithErrors}
				containersErrorMessages={containersErrorMessages}
				isAddingNewSegment={isAddingNewSegmentRef.current}
				onAccordionToggle={handleAccordionToggle}
				forceExpandErrors={formValidationTriggered}
				userClosedAccordions={
					formValidationTriggered ? {} : userClosedAccordions
				}
				validationId={validateFields}
			/>

			<Dialog
				open={editModalOpen}
				onClose={() => {
					setEditModalOpen(false);
					if (hasSyncChanges) {
						onTranslationsModalClose();
					}
				}}
				maxWidth="md"
				fullWidth
				BackdropProps={{ style: { backgroundColor: "#0000004D" } }}
			>
				<Zoom in={editModalOpen}>
					<Box p={5}>
						<Box display="flex" alignItems="center">
							<IconButton
							    id='close-edit-translations'
								onClick={() => {
									setEditModalOpen(false);
									if (hasSyncChanges) {
										onTranslationsModalClose();
									}
								}}
							>
								<Close />
							</IconButton>
							<Typography variant="h6" style={{ marginLeft: 8 }}>
								{resources["EditSegmentTranslations"]}
							</Typography>
						</Box>
						<Box mt={2}>
							{modalTranslations && modalTranslations.length > 0 ? (
								<TranslationsTable
									data={modalTranslations}
									languages={languagesConst}
									defaultLang={selectedLang || "nl"}
									onTranslationSync={syncTranslation}
									resources={resources}
									onDeleteTranslation={deleteTranslation}
									baseUrl={baseUrl}
									syncTranslationKeys={syncTranslationKeys}
									hasTranslationLicence={hasTranslationLicence}
								/>
							) : (
								<Typography variant="body2">
									{resources["NoTranslationsFound"]}
								</Typography>
							)}
						</Box>
					</Box>
				</Zoom>
			</Dialog>

			<SaveNewItemsConfirmationModal
				open={showSaveConfirmation}
				onClose={() => {
					setShowSaveConfirmation(false);
					setPendingEditSegment(null);
				}}
				onConfirm={handleConfirmSaveNewItems}
				resources={resources}
				message={resources["SaveNewItemsConfirmationMessage"]}
				loading={confirmationLoading}
			/>
		</>
	);
}
